import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { ZohoLinkedService, zohoAuthType } from 'app/model/linkedservice/zoho.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { zohoReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ZohoLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                endpoint: '*',
                accessToken: 'secret',
                useEncryptedEndpoints: '*',
                useHostVerification: '*',
                usePeerVerification: '*',
                encryptedCredential: '*',
                connectionProperties: {
                    endpoint: '*',
                    accessToken: 'secret',
                    authenticationType: '*',
                    clientId: '*',
                    clientSecret: 'secret',
                    refreshToken: 'secret',
                    useEncryptedEndpoints: '*',
                    useHostVerification: '*',
                    usePeerVerification: '*',
                    encryptedCredential: '*',
                    accountsUrl: '*'
                }
            }
        }
    }
})
export class ZohoLinkedServiceAdapter extends LinkedServiceAdapter<ZohoLinkedService> {
    protected modelConstructor(): new () => ZohoLinkedService {
        return ZohoLinkedService;
    }

    protected model(): IModel {
        return ZohoLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: ZohoLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.ZohoLinkedService;
        properties.type = Metamodel.modelFor(ZohoLinkedService).name;

        if (linkedService.isOldResource) {
            properties.typeProperties = {
                endpoint: linkedService.endpoint,
                accessToken: passwordOrAKVtoSecretBase(linkedService.accessToken, this.context),
                useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
                useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
                usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
            };
        } else {
            properties.typeProperties = {
                connectionProperties: {
                    authenticationType: linkedService.authType,
                    endpoint: linkedService.endpoint,
                    accessToken: passwordOrAKVtoSecretBase(linkedService.accessToken, this.context),
                    useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
                    useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
                    usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
                }
            };
            if (linkedService.authType === zohoAuthType.OAuth_2_0) {
                properties.typeProperties.connectionProperties.clientId = linkedService.clientId;
                properties.typeProperties.connectionProperties.clientSecret = passwordOrAKVtoSecretBase(linkedService.clientSecret, this.context);
                properties.typeProperties.connectionProperties.refreshToken = passwordOrAKVtoSecretBase(linkedService.refreshToken, this.context);
                properties.typeProperties.connectionProperties.accountsUrl = linkedService.accountsUrl ? linkedService.accountsUrl : undefined;
            }
            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties.connectionProperties[field.key] = field.value;
                });
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: ZohoLinkedService): Promise<ZohoLinkedService> {
        const properties = linkedServiceResource.properties as Model.ZohoLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.ZohoLinkedServiceTypeProperties = properties.typeProperties;

        if (typeProperties.connectionProperties) {
            linkedService.isOldResource = false;
            const connectionProperties = typeProperties.connectionProperties;
            linkedService.authType = connectionProperties.authenticationType;
            linkedService.endpoint = connectionProperties.endpoint;
            linkedService.accessToken = secretBaseToPasswordOrAKV(connectionProperties.accessToken, true, this.context);
            linkedService.useEncryptedEndpoints = connectionProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = connectionProperties.useHostVerification;
            linkedService.usePeerVerification = connectionProperties.usePeerVerification;
            if (linkedService.authType === zohoAuthType.OAuth_2_0) {
                linkedService.clientId = connectionProperties.clientId;
                linkedService.clientSecret = secretBaseToPasswordOrAKV(connectionProperties.clientSecret, true, this.context);
                linkedService.refreshToken = secretBaseToPasswordOrAKV(connectionProperties.refreshToken, true, this.context);
                linkedService.accountsUrl = connectionProperties.accountsUrl;
            }
            linkedService.additionalFields = [];
            Object.keys(connectionProperties).forEach((key) => {
                if (zohoReservedKeys.indexOf(key) < 0) {
                    linkedService.additionalFields.push({ key, value: connectionProperties[key] });
                    delete connectionProperties[key];
                }
            });
        } else {
            linkedService.isOldResource = true;
            linkedService.endpoint = typeProperties.endpoint;
            linkedService.accessToken = secretBaseToPasswordOrAKV(typeProperties.accessToken, true, this.context);
            linkedService.useEncryptedEndpoints = typeProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = typeProperties.useHostVerification;
            linkedService.usePeerVerification = typeProperties.usePeerVerification;
        }

        return linkedService;
    }
}
